import './styles.scss';

import { ReactElement, useEffect, useMemo, useState } from 'react';

import { Button } from 'react-bootstrap';
import DriveTimePdf from '@/features/PdfLayout/layouts/DriveTimePdf';
import Invoice from '@/features/PdfLayout/layouts/Invoice';
import { LoadingBlur } from '@/components/LoadingSpinner';
import TrainingReportPdf from '@/features/PdfLayout/layouts/TrainingReport';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import { useParams } from 'react-router-dom';

export type PdfLayoutState = {
  loading: boolean;
  data: unknown;
  error: unknown;
};
export const initPdfLayoutState: PdfLayoutState = {
  loading: true,
  data: null,
  error: null,
};
export const usePdfLayout = createComponentState(initPdfLayoutState);

const reportLayouts: Record<string, (props: { id?: string }) => ReactElement> = {
  training: TrainingReportPdf,
};

const PdfLayout = (): ReactElement => {
  const { type, id } = useParams();
  const [{ loading, error, data }] = usePdfLayout(({ state: { loading, error, data } }) => [{ loading, error, data }]);
  const [renderComplete, setRenderComplete] = useState(false);

  const Layout = useMemo(() => {
    switch (type) {
      case 'invoice':
        return Invoice;
      case 'dtt':
        return DriveTimePdf;
      // Reports live under /pdf/reports/<report>, e.g. /pdf/reports/training
      case 'reports':
        return reportLayouts[id] || UnknownPdf;
      default:
        return UnknownPdf;
    }
  }, [type, id]);

  useEffect(() => {
    // Mark rendering as complete after all content has been painted
    // This will be used by BE to know when to capture the PDF
    // Require data to be present so the rAF is never scheduled during the brief window
    // where loading is false before the actual fetch begins (both hooks start as loading=false).
    // Do not mark as ready if there was an error (GQL errors return HTTP 200 via Apollo)
    if (!data || loading || error || renderComplete) return;
    const frameId = requestAnimationFrame(() => {
      setRenderComplete(true);
    });
    // Cancel the pending rAF if data clears, loading restarts, or an error is set before it fires
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [data, loading, error, renderComplete]);

  return (
    <div className={getClasses('PdfLayout', type !== 'invoice' && !error && !loading ? 'PdfReady' : '')}>
      <div className="PdfLayout-Content">
        <LoadingBlur className="PdfLoading" fitViewport loading={loading ?? true} />
        <Layout id={id} />
      </div>
      <div className="PdfLayout-Controls d-flex flex-column gap-2">
        <div className="d-flex justify-content-end gap-2">
          <span>PDF Ready:</span>
          <span>
            <i className="fa fa-circle" style={{ color: (type !== 'invoice' || renderComplete) && !error && !loading ? 'green' : 'red' }} />
          </span>
        </div>
        <div className="d-flex justify-content-end">
          <Button className="PdfLayout-Test rounded-pill d-flex gap-2 align-items-center shadow" onClick={window.print} variant="primary">
            <span>Save PDF</span>
            <i className="fa fa-print" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const UnknownPdf = (): ReactElement => {
  const [setLoading] = usePdfLayout(({ setState }) => [(loading: boolean) => setState((state) => ({ ...state, loading }))]);

  useEffect(() => {
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="UnknownPdf">
      <h3>Unknown PDF</h3>
      <p>The requested PDF type is not recognized.</p>
    </div>
  );
};

export default PdfLayout;
